<template>
  <div :id="componentData.uid" :data-magellan-target="componentData.uid" :style="colorStyles">
    <div :class="[collectionRowClasses, componentData.divClass]">
      <section
        class="editorial-group items-stretch flex-col md:flex-row gap-4"
        :class="[componentRowClasses, layoutClasses, typeNameClass]"
      >
        <component
          :is="getComponentForType(item.typeCode)"
          v-for="item in components"
          :key="item.uid"
          :component="item"
        />
      </section>
    </div>
  </div>
</template>

<script>
import {getComponentForType} from '@/modules/CmsComponentMapper.js';
import CmsApi from '@/api/cms';
import {mapGetters} from 'vuex';

const EditorialTypes = {
  AUTO_RESIZE: 'AUTO_RESIZE',
  ONE_UP: 'ONEUP',
  TWO_UP: 'TWOUP',
  THREE_UP: 'THREEUP',
  FOUR_UP: 'FOURUP',
  CONSUMER_MESSAGE_B2C: 'ConsumerMessageB2C',
};

export default {
  props: {
    componentData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      components: [],
      getComponentForType: getComponentForType,
    };
  },
  computed: {
    ...mapGetters('backend', ['occUrlParams']),

    booleanConversions() {
      return {
        isStatic: this.componentData?.isStatic?.toLowerCase() === 'true',
        removePadding: this.componentData?.removePadding?.toLowerCase() === 'true',
        lockAspectRatio: this.componentData?.lockAspectRatio?.toLowerCase() === 'true',
      };
    },
    colorStyles() {
      return {
        ...(this.componentData?.backgroundColor && {backgroundColor: `#${this.componentData?.backgroundColor}`}),
        ...(this.componentData?.editorialTextColor && {color: `#${this.componentData?.editorialTextColor}`}),
      };
    },
    collectionRowClasses() {
      return {
        'py-7 px-1 container': !this.booleanConversions.removePadding,
      };
    },
    componentRowClasses() {
      return {
        block: this.booleanConversions.isStatic,
        'mb-7': !this.booleanConversions.removePadding,
      };
    },
    typeNameClass() {
      return this.componentData?.editorialGroup?.toLowerCase();
    },
    componentsLength() {
      return this.componentNames?.length;
    },
    layoutClasses() {
      if (this.componentData?.editorialGroup === EditorialTypes.AUTO_RESIZE) {
        if (this.componentsLength < 3) {
          return 'w-full md:grid grid-cols-2 gap-x-6';
        } else if (this.componentsLength == 3) {
          return 'w-full md:grid grid-cols-3 gap-x-6';
        } else {
          return 'w-full md:grid grid-cols-4 gap-x-6';
        }
      }

      if (this.booleanConversions.lockAspectRatio) {
        return 'trek-marquee--fixed-aspect';
      }

      if (this.componentData?.editorialGroup === EditorialTypes.ONE_UP) {
        return 'w-full';
      } else if (this.componentData?.editorialGroup === EditorialTypes.TWO_UP) {
        return 'w-full md:grid grid-cols-2 gap-x-6';
      } else if (
        [EditorialTypes.THREE_UP, EditorialTypes.CONSUMER_MESSAGE_B2C].some(
          (type) => type === this.componentData?.editorialGroup
        )
      ) {
        return 'w-full md:grid grid-cols-3 gap-x-6';
      } else if (this.componentData?.editorialGroup === EditorialTypes.FOUR_UP) {
        return 'w-full md:grid grid-cols-4 gap-x-6';
      }
      return 'w-full';
    },
    componentNames() {
      const componentNames = this.componentData?.simpleCMSComponents;
      return componentNames.split(' ');
    },
  },
  watch: {
    componentNames: {
      handler(value) {
        if (value.length) {
          this.fetchComponents();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchComponents() {
      this.components = await CmsApi.getComponentsByIds(this.occUrlParams.userId, this.componentNames);
    },
  },
};
</script>

<style lang="scss" scoped>
.consumer-message__wrapper {
  margin: theme('spacing.4') auto;
  max-width: theme('screens.xxl');

  .editorial-group {
    margin-bottom: 3.5rem;

    :deep() .editorial-small,
    :deep() article {
      padding: theme('spacing.4');
      width: 100%;
      box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
      border: none;

      @media (screen(md)) {
        padding: theme('spacing.5');
      }

      &__header {
        font-family: theme('fontFamily.heading');
        font-size: theme('fontSize.base');
        font-weight: theme('fontWeight.bold');
        line-height: theme('lineHeight.normal');
        margin-bottom: theme('spacing.2');
      }

      &__text {
        font-size: theme('fontSize.sm');
        color: theme('colors.gray.60');
      }
    }
  }
}
</style>
